import { Stack, useTheme } from "@mui/material";
import { Typography } from "@foodpilot/foods";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useAuditLogLabels, type AuditLogLabels } from "./useAuditLogLabels";

type AuditLogSummaryProps = {
    total: number | undefined;
    displayed: number;
    from: string | undefined;
    to: string | undefined;
};

export const describeAuditLogSummaryWindow = (
    from: string | undefined,
    to: string | undefined,
    labels: Pick<AuditLogLabels, "shortUtc">,
    t: TFunction,
): string => {
    if (from !== undefined) {
        return t("settings.audit_log.summary.window", "{{from}} → {{to}} (UTC)", {
            from: labels.shortUtc(from),
            to: labels.shortUtc(to ?? new Date().toISOString()),
        });
    }

    if (to !== undefined) {
        return t("settings.audit_log.period.until", "Until {{to}} (UTC)", { to: labels.shortUtc(to) });
    }

    return t("settings.audit_log.summary.all_time", "all history");
};

export const AuditLogSummary = (props: AuditLogSummaryProps) => {
    const { total, displayed, from, to } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const labels = useAuditLogLabels();

    if (total === undefined) {
        return null;
    }

    const window = describeAuditLogSummaryWindow(from, to, labels, t);

    return (
        <Stack direction="row" gap={theme.spacing(1)} flexWrap="wrap">
            <Typography variant="small-regular" color="text.secondary">
                {total === 1 ?
                    t("settings.audit_log.summary.entry", "{{total}} entry", { total })
                :   t("settings.audit_log.summary.entries", "{{total}} entries", { total })}
            </Typography>
            <Typography variant="small-regular" color="text.secondary">
                {`· ${window}`}
            </Typography>
            {displayed < total && (
                <Typography variant="small-regular" color="text.secondary">
                    {t("settings.audit_log.summary.displayed", "· {{displayed}} shown", { displayed })}
                </Typography>
            )}
        </Stack>
    );
};
